import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { deleteSalarySlip as deleteFromCloudinary } from './cloudinaryService'

/**
 * Get salary slips for a specific employee
 * @param {string} employeeId - Employee ID
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getUserSalarySlips = async (employeeId) => {
  try {
    const slipsQuery = query(
      collection(db, 'salarySlips'),
      where('employeeId', '==', employeeId),
      orderBy('year', 'desc'),
      orderBy('uploadedAt', 'desc')
    )

    const querySnapshot = await getDocs(slipsQuery)
    const slips = []

    querySnapshot.forEach((doc) => {
      slips.push({
        id: doc.id,
        ...doc.data()
      })
    })

    // Sort by year desc, then by month (using month index)
    slips.sort((a, b) => {
      if (b.year !== a.year) return b.year - a.year
      return getMonthIndex(b.month) - getMonthIndex(a.month)
    })

    return { success: true, data: slips }
  } catch (error) {
    console.error('Get user salary slips error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get all salary slips (admin only)
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getAllSalarySlips = async () => {
  try {
    const slipsQuery = query(
      collection(db, 'salarySlips'),
      orderBy('uploadedAt', 'desc')
    )

    const querySnapshot = await getDocs(slipsQuery)
    const slips = []

    querySnapshot.forEach((doc) => {
      slips.push({
        id: doc.id,
        ...doc.data()
      })
    })

    return { success: true, data: slips }
  } catch (error) {
    console.error('Get all salary slips error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Check if salary slip already exists for employee + month + year
 * @param {string} employeeId - Employee ID
 * @param {string} month - Month name
 * @param {number} year - Year
 * @returns {Promise<{ exists: boolean, docId?: string }>}
 */
export const checkDuplicate = async (employeeId, month, year) => {
  try {
    const duplicateQuery = query(
      collection(db, 'salarySlips'),
      where('employeeId', '==', employeeId),
      where('month', '==', month),
      where('year', '==', year)
    )

    const querySnapshot = await getDocs(duplicateQuery)

    if (!querySnapshot.empty) {
      return {
        exists: true,
        docId: querySnapshot.docs[0].id
      }
    }

    return { exists: false }
  } catch (error) {
    console.error('Check duplicate error:', error)
    // Return false on error to allow upload attempt
    return { exists: false }
  }
}

/**
 * Create new salary slip record
 * @param {object} data - Salary slip data
 * @returns {Promise<{ success: boolean, docId?: string, error?: string }>}
 */
export const createSalarySlip = async (data) => {
  try {
    // Validate required fields
    const requiredFields = [
      'employeeId',
      'employeeName',
      'employeeEmail',
      'month',
      'year',
      'cloudinaryUrl',
      'cloudinaryPublicId',
      'uploadedBy',
      'uploadedByName',
      'fileSize'
    ]

    for (const field of requiredFields) {
      if (!data[field]) {
        return { success: false, error: `Missing required field: ${field}` }
      }
    }

    // Check for duplicate
    const duplicate = await checkDuplicate(data.employeeId, data.month, data.year)
    if (duplicate.exists) {
      return {
        success: false,
        error: `Salary slip for ${data.month} ${data.year} already exists for this employee`
      }
    }

    // Add uploaded timestamp
    const uploadedAt = Timestamp.now()
    const salarySlipData = {
      ...data,
      uploadedAt: uploadedAt,
      createdAt: new Date().toISOString()
    }

    // Create document
    const docRef = await addDoc(collection(db, 'salarySlips'), salarySlipData)

    return {
      success: true,
      docId: docRef.id,
      uploadedAt: uploadedAt  // Return timestamp for immediate UI update
    }
  } catch (error) {
    console.error('Create salary slip error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Delete salary slip (from both Firestore and Cloudinary)
 * @param {string} docId - Firestore document ID
 * @param {string} cloudinaryPublicId - Cloudinary public ID
 * @returns {Promise<{ success: boolean, warning?: string, deletedFromFirestore?: boolean, deletedFromCloudinary?: boolean, error?: string }>}
 */
export const deleteSalarySlip = async (docId, cloudinaryPublicId) => {
  try {
    // Delete from Firestore
    await deleteDoc(doc(db, 'salarySlips', docId))

    // Attempt to delete from Cloudinary
    // (Note: This requires server-side implementation for full deletion)
    const cloudResult = await deleteFromCloudinary(cloudinaryPublicId)

    return {
      success: true,
      warning: cloudResult?.warning,
      deletedFromFirestore: true,
      deletedFromCloudinary: cloudResult?.deletedFromCloudinary ?? false
    }
  } catch (error) {
    console.error('Delete salary slip error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get all employees from users collection (for admin dropdown)
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getAllEmployees = async () => {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      where('status', '==', 'active')
    )

    const querySnapshot = await getDocs(usersQuery)
    const employees = []

    querySnapshot.forEach((doc) => {
      const data = doc.data()
      employees.push({
        uid: doc.id,
        employeeId: data.employeeId,
        fullName: data.fullName,
        email: data.email,
        role: data.role || 'employee'
      })
    })

    // Sort by full name
    employees.sort((a, b) => a.fullName.localeCompare(b.fullName))

    return { success: true, data: employees }
  } catch (error) {
    console.error('Get all employees error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Helper: Get month index for sorting (January = 0, December = 11)
 * @param {string} monthName - Month name
 * @returns {number} - Month index
 */
const getMonthIndex = (monthName) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  return months.indexOf(monthName)
}

/**
 * Helper: Get month name from index
 * @param {number} index - Month index (0-11)
 * @returns {string} - Month name
 */
export const getMonthName = (index) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  return months[index] || 'Unknown'
}

/**
 * Helper: Get list of all months
 * @returns {array} - Array of month names
 */
export const getAllMonths = () => {
  return [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
}

/**
 * Helper: Get year options (current year +/- 2 years)
 * @returns {array} - Array of years
 */
export const getYearOptions = () => {
  const currentYear = new Date().getFullYear()
  const years = []

  for (let i = currentYear + 1; i >= currentYear - 2; i--) {
    years.push(i)
  }

  return years
}

/**
 * Helper: Format Firestore timestamp to readable date
 * Handles both Firestore Timestamp objects and Date objects/ISO strings
 * @param {Timestamp|Date|string} timestamp - Firestore timestamp, Date object, or ISO string
 * @returns {string} - Formatted date
 */
export const formatUploadDate = (timestamp) => {
  if (!timestamp) return 'Unknown'

  try {
    let date

    // Handle Firestore Timestamp object
    if (timestamp && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate()
    }
    // Handle Date object
    else if (timestamp instanceof Date) {
      date = timestamp
    }
    // Handle ISO string or timestamp number
    else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      date = new Date(timestamp)
    }
    // Unknown format
    else {
      return 'Unknown'
    }

    // Validate date
    if (isNaN(date.getTime())) {
      return 'Unknown'
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  } catch (error) {
    console.error('Format upload date error:', error)
    return 'Unknown'
  }
}
